"use client";

/**
 * Arms the design-system preview flags from a URL param, once, on load.
 *
 * The state itself lives in `dsNextState` — this file only translates
 * `?ds=` and `?font=` into it. The place a person actually flips these is the
 * control on `/prototypes`, which is staff-only and can afford to be visible;
 * a control in the app chrome would be a control every user can find, on a
 * feature meant for looking at an unfinished design.
 *
 * THE PARAMS ARE COMMANDS, NOT STATE. Each one sets the preference and is then
 * stripped from the URL, so a link shared with `?font=geometric` in it does not
 * keep re-arming on someone else's machine every time they open it. The
 * preference lives in `localStorage` from that point on.
 *
 * Their one weakness, worth knowing: a server redirect eats them. Loading
 * `/?ds=next` redirects to `/discovery` and the param is gone before this ever
 * runs. Aim them at a URL that does not redirect, or use the control.
 *
 * WHAT IT COSTS everyone else: one effect that reads two `localStorage` keys
 * on mount, and nothing rendered. Off is the default, and a browser that has
 * never set either key never takes another branch.
 */

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import {
  applyFlags,
  type Flags,
  isFontChoice,
  readFlags,
  writeFlags,
} from "./dsNextState";

export function DsNextFlag({ enabled }: { enabled: boolean }) {
  /*
   * `/prototypes` runs its own shipped-versus-candidate comparison, wearing
   * `ds-next` on one side of a split view and deliberately not on the other.
   * A geometry flag inherited from <html> reaches BOTH sides — custom
   * properties inherit, and the shipped column has no way to un-inherit them —
   * so the "current" column would quietly render candidate radii, transparent
   * borders and pane shadows. That is the one thing that surface exists to
   * show, so the product flag stays out of it.
   *
   * The typeface is not withheld: it moves both columns equally, so it cannot
   * corrupt a geometry comparison, and seeing the candidate face on the
   * prototypes is useful in its own right.
   *
   * Read reactively rather than once. This component lives in `Providers` and
   * survives client-side navigation, so a route change has to re-evaluate —
   * otherwise arming the flag on a product page and then navigating to
   * /prototypes carries `ds-next` in with it.
   */
  const pathname = usePathname();
  const onPrototypes = pathname?.startsWith("/prototypes") ?? false;

  useEffect(() => {
    /*
     * Not on the allowlist: clear, do not merely skip.
     *
     * The preference lives in this browser's storage, so someone who had
     * access and lost it would otherwise keep the preview forever — the flag
     * would be off in the sense that nothing re-arms it, and on in the sense
     * that every page still wears it. Applying the empty state makes the gate
     * take effect on the next load rather than on the next cache clear.
     *
     * The stored keys are deliberately left alone: revoking a preview should
     * not also forget the preference, in case access comes back.
     */
    if (!enabled) {
      applyFlags({ next: false, font: null });
      return;
    }

    const url = new URL(window.location.href);
    const askedDs = url.searchParams.get("ds");
    const askedFont = url.searchParams.get("font");

    const stored = readFlags();
    const next: Flags = { ...stored };
    let consumed = false;

    if (askedDs === "next" || askedDs === "off") {
      next.next = askedDs === "next";
      url.searchParams.delete("ds");
      consumed = true;
    }

    if (isFontChoice(askedFont) || askedFont === "off") {
      next.font = askedFont === "off" ? null : askedFont;
      url.searchParams.delete("font");
      consumed = true;
    }

    if (consumed) {
      writeFlags(next);
      // Rewritten once, after both params. Doing it inside a branch means a
      // request carrying only the other param never gets stripped — which
      // looks exactly like the flag failing to run.
      window.history.replaceState(null, "", url.toString());
    }

    applyFlags(onPrototypes ? { ...next, next: false } : next);
  }, [enabled, onPrototypes]);

  return null;
}
